/**
 * SignIn Page Component
 *
 * A comprehensive sign-in page that matches the existing design system
 * and integrates with the backend authentication API.
 */

import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import { useUserData } from "../../hooks/useUserData";
import { APP_CONFIG } from "../../configs/config";
import { useTypographyHelpers } from "../../contexts/DesignContext";
import { performPostAuthRedirect } from "../../utils/redirectUtils";
import Header from "../layout/Header";
import Footer from "../layout/Footer";
import SectionWrapper from "../ui/SectionWrapper";
import LoadingSpinner from "../ui/LoadingSpinner";
import { useSignInForm } from "./signin/useSignInForm";
import SignInForm from "./signin/SignInForm";
import {
  createHandleSubmit,
  createHandleGoogleSuccess,
  handleGoogleError,
} from "./signin/signInHandlers";

const SignInPage = () => {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const location = useLocation();
  const {
    login,
    loginWithGoogle,
    isAuthenticated,
    isLoading: authLoading,
  } = useAuth();
  const { getHeaderProps } = useUserData();
  const { getPrimaryHeading, getSupportingText } = useTypographyHelpers();

  // Use custom hook for form state
  const {
    formData,
    formErrors,
    setFormErrors,
    isLoading,
    setIsLoading,
    submitError,
    setSubmitError,
    showPassword,
    successMessage,
    searchParams,
    handleChange,
    toggleShowPassword,
  } = useSignInForm();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      performPostAuthRedirect(searchParams, location.state, navigate);
    }
  }, [isAuthenticated, authLoading, navigate, searchParams, location.state]);

  // Handle form submission
  const handleSubmit = createHandleSubmit(
    formData,
    setFormErrors,
    setIsLoading,
    setSubmitError,
    login,
    searchParams,
    location.state,
    navigate,
    t,
  );

  // Handle Google login success
  const handleGoogleSuccess = createHandleGoogleSuccess(
    setIsLoading,
    setSubmitError,
    loginWithGoogle,
    searchParams,
    location.state,
    navigate,
    t,
  );

  // Show loading spinner if auth is initializing
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-violet-900 flex flex-col items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-violet-900 flex flex-col">
      <Header
        {...(isAuthenticated
          ? getHeaderProps()
          : { username: null, user_image_url: null })}
        logoUrl={APP_CONFIG.LOGO_URL}
      />

      <main className="flex-1">
        <SectionWrapper>
          <div className="max-w-md mx-auto">
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className={getPrimaryHeading("hero", true)}>
                {t("signIn.title")}
              </h1>
              <p className={getSupportingText("description")}>
                {t("signIn.subtitle")}
              </p>
            </div>

            <SignInForm
              formData={formData}
              formErrors={formErrors}
              isLoading={isLoading}
              submitError={submitError}
              successMessage={successMessage}
              showPassword={showPassword}
              handleChange={handleChange}
              toggleShowPassword={toggleShowPassword}
              handleSubmit={handleSubmit}
              handleGoogleSuccess={handleGoogleSuccess}
              handleGoogleError={() => handleGoogleError(null, t)}
            />
          </div>
        </SectionWrapper>
      </main>

      <Footer logoUrl={APP_CONFIG.LOGO_URL} />
    </div>
  );
};

export default SignInPage;
